import { isSameDay, format } from "date-fns";
import { useEffect, useMemo } from "react";
import {
    ETimeSlot,
    IAvailableAppointmentDate,
} from "@modules/eirCustomersFacade/interfaces/models/IAvailableAppointmentDate";
import {
    buildMap,
    getFirstAvailableDateInList,
    getFirstAvailableTimeSlot,
    getValueOfMap,
    sortTimeSlots,
} from "@features/Appointment/utils";
import { SelectChangeEvent } from "@mui/material";
import { useDeviceType } from "@hook/useDeviceType";
import { EInstallationType } from "@modules/eirCustomersFacade/interfaces/models/IAppointment";

import { API_DATE_FORMAT } from "@constants";

interface IParams {
    appointmentInstallationType: EInstallationType;
    availableAppointmentList: IAvailableAppointmentDate[];
    dateValue: string;
    setDateValue(date: string): void;
    setTimeSlotValue(timeSlot: ETimeSlot): void;
}

export const useAppointmentRescheduledModalContent = (props: IParams) => {
    const {
        appointmentInstallationType,
        availableAppointmentList: availableDateList,
        dateValue,
        setDateValue,
        setTimeSlotValue,
    } = props;

    const { isMobile } = useDeviceType();
    const isTechInstallation = [EInstallationType.FULL, EInstallationType.TECH].includes(appointmentInstallationType);

    const availableAppointmentMap = buildMap(availableDateList);

    const firstAvailableDate = getFirstAvailableDateInList(availableDateList);

    const firstAvailableTimeSlot = getFirstAvailableTimeSlot(
        getValueOfMap(availableAppointmentMap, firstAvailableDate)
    );

    useEffect(() => {
        setDateValue(firstAvailableDate);
        setTimeSlotValue(firstAvailableTimeSlot);
    }, [firstAvailableDate, firstAvailableTimeSlot, setDateValue, setTimeSlotValue]);

    const timeSlotAvailableListForDate = useMemo(
        () => sortTimeSlots(getValueOfMap(availableAppointmentMap, dateValue)),
        [availableAppointmentMap, dateValue]
    );

    const shouldDisableDate = (date: Date) => {
        if (availableDateList.length === 0) {
            return false;
        }

        return !availableDateList.some((availableDate) => isSameDay(new Date(availableDate.scheduledDate), date));
    };

    const handleDateChange = (date: Date) => {
        const formattedDate = format(date, API_DATE_FORMAT);
        setDateValue(formattedDate);
        setTimeSlotValue(getFirstAvailableTimeSlot(getValueOfMap(availableAppointmentMap, formattedDate)));
    };
    const handleTimeSlotChange = (event: SelectChangeEvent<ETimeSlot>) => {
        setTimeSlotValue(event.target.value as ETimeSlot);
    };

    return {
        isMobile,
        isTechInstallation,
        shouldDisableDate,
        handleDateChange,
        handleTimeSlotChange,
        timeSlotAvailableListForDate,
    };
};
